import { ConfigurationContext } from '@portals/fifi/src/contexts/configuration';
import { GeoJsonFeature, useYellowMapsAutoComplete } from '@portals/maps/src/hooks/useYellowMapsAutoComplete';
import axios from 'axios';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { SiteSearchCategory } from '../../models';
import { useDebounce } from '../hooks/useDebounce';
import { useOptionalRouter } from '../hooks/useOptionalRouter';

export type Search = {
  suggests: {
    [key: string]:
      | { type: 'sitesearch'; items: Array<SearchItem> }
      | { type: 'jobmarket'; items: Array<unknown> }
      | { type: 'fifi'; items: Array<GeoJsonFeature> };
  };
  totalResultsPerContentGroup: {
    [key: string]: number;
  };
};

export type SiteSearchContextValue = {
  search: Search | null;
  siteSearchCategories: Array<SiteSearchCategory> | null;
  inputValue: string;
  onInputChange: (value: string) => void;
};

const SiteSearchContext = React.createContext<SiteSearchContextValue | null>(null);

export { SiteSearchContext };

export type SearchItem = {
  content: string;
  image: string | undefined;
  link: string;
  name: string;
  description: string | undefined;
  overline: string | undefined;
};

interface Props {
  children: React.ReactNode;
  initialSiteSearchCategories: Array<SiteSearchCategory> | null;
}

export const SiteSearchContextProvider: React.FC<Props> = ({ children, initialSiteSearchCategories = null }) => {
  const router = useOptionalRouter();
  const query = router.query.query ? router.query.query.toString() : '';
  const [inputValue, setInputValue] = useState<string>(query ? query.toString() : '');
  const debouncedInputValue = useDebounce(inputValue);
  const [search, setSearch] = useState<Search | null>(null);
  const [siteSearchCategories, setSiteSearchCategories] = useState<Array<SiteSearchCategory> | null>(
    initialSiteSearchCategories
  );
  const contextConfig = useContext(ConfigurationContext);
  const { features } = useYellowMapsAutoComplete(contextConfig, {
    query: inputValue,
    filterOptions: {
      excludedGeoEntities: ['STATE', 'COUNTRY', 'ATTRACTION'],
    },
  });

  useEffect(() => {
    setInputValue(query);
    // SCSTP1-541
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    if (debouncedInputValue) {
      urlSearchParams.set('query', debouncedInputValue);
    } else {
      urlSearchParams.delete('query');
    }

    router.push(
      {
        pathname: window.location.pathname,
        search: urlSearchParams.toString(),
      },
      undefined,
      { shallow: true }
    );
    // ignore router dependency because router would change on every route change and lead to an infinite loop open issue:
    // https://github.com/vercel/next.js/issues/18127
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedInputValue]);

  useEffect(() => {
    if (initialSiteSearchCategories) {
      return;
    }

    const cancelToken = axios.CancelToken.source();
    const getSiteSearchCategories = async () => {
      const { data } = await axios.get('/api/site-search-categories', {
        cancelToken: cancelToken.token,
      });
      setSiteSearchCategories(data);
    };

    getSiteSearchCategories().catch((e) => {
      // eslint-disable-next-line no-console
      console.error(e);
    });

    return () => {
      cancelToken.cancel('Operation canceled by user.');
    };
  }, [initialSiteSearchCategories]);

  useEffect(() => {
    if (!query) {
      setSearch(null);
      return;
    }

    const cancelToken = axios.CancelToken.source();
    const getSearch = async () => {
      const { data } = await axios.get('/api/search', {
        cancelToken: cancelToken.token,
        params: { query },
      });
      setSearch(data);
    };

    getSearch().catch((e) => {
      // eslint-disable-next-line no-console
      console.error(e);
    });

    return () => {
      cancelToken.cancel('Operation canceled by user.');
    };
  }, [query]);

  const onInputChange = useCallback((newInputValue: string) => {
    setInputValue(newInputValue);
  }, []);

  const searchMemo: Search = useMemo(() => {
    if (!search) {
      return null;
    }

    return {
      suggests: {
        ...search.suggests,
        fifi: {
          type: 'fifi',
          items: features,
        },
      },
      totalResultsPerContentGroup: search.totalResultsPerContentGroup,
    };
  }, [search, features]);

  return (
    <SiteSearchContext.Provider value={{ search: searchMemo, siteSearchCategories, inputValue, onInputChange }}>
      {children}
    </SiteSearchContext.Provider>
  );
};

export const useSiteSearchContext = (): SiteSearchContextValue => {
  const context = useContext(SiteSearchContext);

  if (context === null) {
    throw new Error('useSiteSearchContext must be used within a SiteSearchContextProvider');
  }

  return context;
};
